import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { ShieldCheck, ShieldOff, Loader2, Search, Eye, X, Download } from "lucide-react";
import { toast } from "sonner";

export default function AdminSepaMandates() {
  return (
    <Layout title="SEPA-Lastschriftmandate" subtitle="Alle online erteilten Mandate · Status, Bankverbindung, Widerruf">
      <AdminSepaMandatesContent />
    </Layout>
  );
}

export function AdminSepaMandatesContent() {
  const [mandates, setMandates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? `?status_filter=${filter}` : "";
      const { data } = await api.get(`/sepa/admin/mandates${params}`);
      setMandates(data.mandates || []);
    } catch (e) {
      toast.error(apiError(e, "Mandate konnten nicht geladen werden"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const filtered = useMemo(() => {
    if (!q.trim()) return mandates;
    const needle = q.trim().toLowerCase();
    return mandates.filter((m) =>
      [m.mandate_reference, m.user_name, m.user_email, m.debtor_name, m.iban_masked]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(needle))
    );
  }, [mandates, q]);

  const counts = useMemo(() => ({
    total: mandates.length,
    active: mandates.filter((m) => m.status === "active").length,
    revoked: mandates.filter((m) => m.status === "revoked").length,
  }), [mandates]);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/sepa/admin/mandates/${id}`);
      setDetail(data);
    } catch (e) {
      toast.error(apiError(e, "Detail konnte nicht geladen werden"));
    }
  };

  const revoke = async (id) => {
    const reason = window.prompt("Grund für Widerruf (optional)?") ?? null;
    if (reason === null && !window.confirm("Mandat wirklich widerrufen?")) return;
    try {
      await api.post(`/sepa/admin/mandates/${id}/revoke`, { reason: reason || "" });
      toast.success("Mandat widerrufen");
      setDetail(null);
      load();
    } catch (e) {
      toast.error(apiError(e, "Widerruf fehlgeschlagen"));
    }
  };

  const downloadPdf = async (id, ref) => {
    try {
      const res = await api.get(`/sepa/admin/mandates/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `SEPA-Mandat_${ref || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e, "PDF-Download fehlgeschlagen"));
    }
  };

  return (
    <>
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        <Kpi label="Gesamt" value={counts.total} tone="zinc" testid="sepa-kpi-total" onClick={() => setFilter("all")} active={filter === "all"} />
        <Kpi label="Aktiv" value={counts.active} tone="emerald" testid="sepa-kpi-active" onClick={() => setFilter("active")} active={filter === "active"} />
        <Kpi label="Widerrufen" value={counts.revoked} tone="zinc" testid="sepa-kpi-revoked" onClick={() => setFilter("revoked")} active={filter === "revoked"} />
      </div>

      {/* Search */}
      <div className="mb-4 relative max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Suche nach User, Mandatsreferenz, IBAN…"
          className="pl-9 h-10"
          data-testid="sepa-search"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-zinc-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade…</div>
      ) : (
        <>
        <div className="hidden xl:block rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="sepa-mandates-table">
          <div className="grid grid-cols-12 px-5 py-3 text-[11px] uppercase tracking-[0.2em] font-bold text-zinc-500 border-b border-zinc-200 bg-zinc-50">
            <div className="col-span-3">Referenz / User</div>
            <div className="col-span-3">Kontoinhaber</div>
            <div className="col-span-2">IBAN</div>
            <div className="col-span-2">Erteilt am</div>
            <div className="col-span-1 text-center">Status</div>
            <div className="col-span-1 text-right">Aktion</div>
          </div>
          {filtered.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-zinc-500" data-testid="sepa-empty">Keine Mandate gefunden</div>
          ) : filtered.map((m) => (
            <div key={m.id} className="grid grid-cols-12 px-5 py-3 items-center border-b border-zinc-200 last:border-0 hover:bg-zinc-50/40" data-testid={`sepa-row-${m.mandate_reference}`}>
              <div className="col-span-3 min-w-0">
                <div className="font-mono text-[13px] font-semibold truncate">{m.mandate_reference}</div>
                <div className="text-[11px] text-zinc-500 truncate">{m.user_email}</div>
              </div>
              <div className="col-span-3 min-w-0 truncate">
                <div className="text-sm font-medium truncate">{m.debtor_name}</div>
                <div className="text-[11px] text-zinc-500 truncate">{m.debtor_postal_code} {m.debtor_city}</div>
              </div>
              <div className="col-span-2 font-mono text-[12px] truncate">{m.iban_masked}</div>
              <div className="col-span-2 text-[12px] text-zinc-600">{new Date(m.signed_at).toLocaleDateString("de-DE")}</div>
              <div className="col-span-1 text-center">
                {m.status === "active" ? (
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">aktiv</Badge>
                ) : (
                  <Badge variant="outline" className="bg-zinc-100 text-zinc-600 border-zinc-200">widerrufen</Badge>
                )}
              </div>
              <div className="col-span-1 flex items-center justify-end gap-1">
                <Button size="icon" variant="ghost" onClick={() => downloadPdf(m.id, m.mandate_reference)} data-testid={`sepa-pdf-${m.mandate_reference}`} title="PDF herunterladen">
                  <Download className="h-4 w-4 text-cyan-600" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => openDetail(m.id)} data-testid={`sepa-view-${m.mandate_reference}`} title="Details">
                  <Eye className="h-4 w-4" />
                </Button>
                {m.status === "active" && (
                  <Button size="icon" variant="ghost" onClick={() => revoke(m.id)} data-testid={`sepa-revoke-${m.mandate_reference}`} title="Widerrufen">
                    <ShieldOff className="h-4 w-4 text-rose-600" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Mobile/Tablet: Card-Liste */}
        <div className="xl:hidden grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-3" data-testid="sepa-cards-mobile">
          {filtered.length === 0 ? (
            <div className="md:col-span-2 lg:col-span-1 rounded-2xl border border-zinc-200 bg-white px-4 py-8 text-center text-sm text-zinc-500">Keine Mandate gefunden</div>
          ) : filtered.map((m) => (
            <div key={m.id} className="rounded-2xl border border-zinc-200 bg-white p-4" data-testid={`sepa-card-${m.mandate_reference}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-mono text-[13px] font-semibold truncate">{m.mandate_reference}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{m.user_email}</div>
                </div>
                {m.status === "active" ? (
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 shrink-0">aktiv</Badge>
                ) : (
                  <Badge variant="outline" className="bg-zinc-100 text-zinc-600 border-zinc-200 shrink-0">widerrufen</Badge>
                )}
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-2 mt-3 text-xs">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-400">Kontoinhaber</div>
                  <div className="font-medium truncate mt-0.5">{m.debtor_name}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{m.debtor_postal_code} {m.debtor_city}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-400">IBAN</div>
                  <div className="font-mono mt-0.5">{m.iban_masked}</div>
                  <div className="text-[11px] text-zinc-500">Erteilt: {new Date(m.signed_at).toLocaleDateString("de-DE")}</div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-1 mt-2 border-t border-zinc-100 pt-2">
                <Button size="icon" variant="ghost" onClick={() => downloadPdf(m.id, m.mandate_reference)} data-testid={`sepa-pdf-m-${m.mandate_reference}`} title="PDF herunterladen">
                  <Download className="h-4 w-4 text-cyan-600" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => openDetail(m.id)} data-testid={`sepa-view-m-${m.mandate_reference}`} title="Details">
                  <Eye className="h-4 w-4" />
                </Button>
                {m.status === "active" && (
                  <Button size="icon" variant="ghost" onClick={() => revoke(m.id)} data-testid={`sepa-revoke-m-${m.mandate_reference}`} title="Widerrufen">
                    <ShieldOff className="h-4 w-4 text-rose-600" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6 overflow-y-auto" onClick={() => setDetail(null)}>
          <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl my-8" onClick={(e) => e.stopPropagation()} data-testid="sepa-detail-modal">
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-700">SEPA-Mandat</div>
                <div className="font-display text-xl font-bold tracking-tight">{detail.mandate.mandate_reference}</div>
              </div>
              <button onClick={() => setDetail(null)} className="h-8 w-8 rounded-full hover:bg-zinc-100 flex items-center justify-center"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Info label="Status">
                  {detail.mandate.status === "active" ? (
                    <span className="text-emerald-700 inline-flex items-center gap-1"><ShieldCheck className="h-4 w-4" /> Aktiv</span>
                  ) : (
                    <span className="text-zinc-600 inline-flex items-center gap-1"><ShieldOff className="h-4 w-4" /> Widerrufen</span>
                  )}
                </Info>
                <Info label="Erteilt am">{new Date(detail.mandate.signed_at).toLocaleString("de-DE")}</Info>
                <Info label="User">{detail.user?.name} · {detail.user?.email}</Info>
                <Info label="Kontoinhaber">{detail.mandate.debtor_name}</Info>
                <Info label="Adresse">{detail.mandate.debtor_address}, {detail.mandate.debtor_postal_code} {detail.mandate.debtor_city}</Info>
                <Info label="IBAN" mono>{detail.iban_formatted}</Info>
                {detail.mandate.bic && <Info label="BIC" mono>{detail.mandate.bic}</Info>}
                {detail.mandate.bank_name && <Info label="Bank">{detail.mandate.bank_name}</Info>}
                <Info label="Gläubiger-ID" mono>{detail.mandate.creditor_id}</Info>
                {detail.mandate.ip_address && <Info label="IP zur Erteilung" mono>{detail.mandate.ip_address}</Info>}
                {detail.mandate.revoked_at && <Info label="Widerrufen am">{new Date(detail.mandate.revoked_at).toLocaleString("de-DE")}</Info>}
                {detail.mandate.revoked_reason && <Info label="Widerrufs-Grund">{detail.mandate.revoked_reason}</Info>}
              </div>
              <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-3">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2">Unterschrift</div>
                <img src={detail.mandate.signature_data_url} alt="Unterschrift" className="max-h-40 mx-auto bg-white rounded" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => downloadPdf(detail.mandate.id, detail.mandate.mandate_reference)}
                className="text-cyan-700 border-cyan-200"
                data-testid="sepa-detail-pdf"
              >
                <Download className="h-4 w-4 mr-1" /> PDF herunterladen
              </Button>
              {detail.mandate.status === "active" && (
                <Button variant="outline" className="text-rose-600 border-rose-200" onClick={() => revoke(detail.mandate.id)}>
                  <ShieldOff className="h-4 w-4 mr-1" /> Widerrufen
                </Button>
              )}
              <Button onClick={() => setDetail(null)}>Schließen</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Kpi({ label, value, tone = "zinc", onClick, active, testid }) {
  const tones = {
    emerald: "from-emerald-50 to-cyan-50 text-emerald-700 ring-emerald-200",
    zinc: "from-zinc-50 to-zinc-100 text-zinc-700 ring-zinc-200",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left rounded-2xl bg-gradient-to-br ${tones[tone] || tones.zinc} ring-1 px-4 py-4 transition hover:shadow-md hover:-translate-y-0.5 ${active ? "ring-2 ring-offset-2 ring-zinc-900/10" : ""}`}
    >
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">{label}</div>
      <div className="mt-1 text-3xl font-bold text-zinc-900">{value}</div>
    </button>
  );
}

function Info({ label, children, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500">{label}</div>
      <div className={`text-sm font-semibold text-zinc-900 mt-0.5 ${mono ? "font-mono" : ""}`}>{children}</div>
    </div>
  );
}
